/**
 * load-more-advisory.js
 *
 * - On click: fetches next page, appends cards only (skips banner), updates URL
 * - On restore (refresh at ?page=N): fetches page 1 fully (WITH banner at
 *   correct index), then fetches pages 2…N-1 for cards only and appends,
 *   then server-rendered page N cards are already in DOM so just re-orders.
 *
 * Banner strategy:
 *   The banner must come from page 1's server render where Liquid places it
 *   at the correct banner_index position. On restore we:
 *   1. Clear the grid
 *   2. Fetch page 1 fully (cards + banner in correct position)
 *   3. Append pages 2…N-1 cards only
 *   4. Append the already-server-rendered page N cards (save & re-append)
 */

class LoadMoreAdvisory extends HTMLElement {
  constructor() {
    super();
    this.button      = this.querySelector('a');
    this.gridId      = this.dataset.gridId;
    this.sectionId   = this.dataset.sectionId;
    this.currentPage = parseInt(this.dataset.currentPage || '1', 10);

    if (this.button) {
      this.button.addEventListener('click', this.onLoadMore.bind(this));
    }

    this.restoreEarlierPages();
  }

  getPageKey(params) {
    for (const key of params.keys()) {
      if (key === 'page' || key.startsWith('page_')) return key;
    }
    return 'page';
  }

  buildFetchUrl(pageNum, pageKey) {
    const base = window.location.pathname;
    if (this.sectionId) {
      return `${base}?section_id=${this.sectionId}&${pageKey}=${pageNum}`;
    }
    return `${base}?${pageKey}=${pageNum}`;
  }

  // All grid children except the banner
  getCardsOnly(doc) {
    const grid  = doc.getElementById(this.gridId);
    const scope = grid || doc;
    return Array.from(scope.querySelectorAll('.article-card'));
  }

  // All grid children INCLUDING the banner (full page 1 content)
  getAllGridChildren(doc) {
    const grid = doc.getElementById(this.gridId);
    if (!grid) return [];
    return Array.from(grid.children);
  }

  // ─── Restore pages 1…currentPage-1 on refresh ──────────────────────
  async restoreEarlierPages() {
    if (this.currentPage <= 1) return;

    const currentGrid = document.getElementById(this.gridId);
    if (!currentGrid) return;

    this._setLoading(true);

    const pageKey = 'page';

    // Save the server-rendered cards for currentPage (already in DOM)
    const serverCards = Array.from(currentGrid.querySelectorAll('.article-card'));

    // Clear the grid entirely so we can rebuild in correct order
    currentGrid.innerHTML = '';

    // Step 1: Fetch page 1 WITH banner (full grid children, correct order)
    try {
      const url  = this.buildFetchUrl(1, pageKey);
      const res  = await fetch(url);
      const html = await res.text();
      const doc  = new DOMParser().parseFromString(html, 'text/html');
      const children = this.getAllGridChildren(doc);
      const fragment = document.createDocumentFragment();
      children.forEach(child => fragment.appendChild(child));
      currentGrid.appendChild(fragment);
    } catch (e) {
      console.warn('[load-more-advisory] failed to fetch page 1:', e);
    }

    // Step 2: Fetch pages 2…currentPage-1 cards only and append
    for (let p = 2; p < this.currentPage; p++) {
      try {
        const url   = this.buildFetchUrl(p, pageKey);
        const res   = await fetch(url);
        const html  = await res.text();
        const doc   = new DOMParser().parseFromString(html, 'text/html');
        const cards = this.getCardsOnly(doc);
        const fragment = document.createDocumentFragment();
        cards.forEach(card => fragment.appendChild(card));
        currentGrid.appendChild(fragment);
      } catch (e) {
        console.warn('[load-more-advisory] restore page failed:', p, e);
      }
    }

    // Step 3: Re-append the server-rendered currentPage cards
    const fragment = document.createDocumentFragment();
    serverCards.forEach(card => fragment.appendChild(card));
    currentGrid.appendChild(fragment);

    // Re-enable button
    this._setLoading(false);
  }

  // ─── Load more click ────────────────────────────────────────────────
  onLoadMore(event) {
    event.preventDefault();

    const button  = this.button;
    const nextUrl = button.href;

    this._setLoading(true);

    const query    = nextUrl.includes('?') ? nextUrl.slice(nextUrl.indexOf('?') + 1) : '';
    const params   = new URLSearchParams(query);
    const pageKey  = this.getPageKey(params);
    const nextPage = params.get(pageKey) || '2';

    const fetchUrl = this.buildFetchUrl(nextPage, pageKey);

    fetch(fetchUrl)
      .then(res => res.text())
      .then(html => {
        const doc         = new DOMParser().parseFromString(html, 'text/html');
        const currentGrid = document.getElementById(this.gridId);

        // Append cards only — never re-append the banner
        const cards = this.getCardsOnly(doc);
        cards.forEach(card => currentGrid.appendChild(card));

        const nextLoadMore = doc.querySelector('load-more-advisory');
        if (nextLoadMore) {
          this.innerHTML   = nextLoadMore.innerHTML;
          this.currentPage = parseInt(nextPage, 10);
          this.button      = this.querySelector('a');
          if (this.button) {
            this.button.addEventListener('click', this.onLoadMore.bind(this));
          }
        } else {
          this.remove();
        }

        window.history.replaceState(
          {},
          '',
          `${window.location.pathname}?${pageKey}=${nextPage}`
        );
      })
      .catch(() => {
        this._setLoading(false);
      });
  }

  _setLoading(isLoading) {
    if (!this.button) return;
    const labelEl = this.button.querySelector('[data-label]');
    if (isLoading) {
      this.button.setAttribute('disabled', '');
      this.button.setAttribute('aria-busy', 'true');
      if (labelEl) labelEl.textContent = this.button.dataset.loadingText || 'Loading...';
    } else {
      this.button.removeAttribute('disabled');
      this.button.setAttribute('aria-busy', 'false');
      if (labelEl) labelEl.textContent = this.button.dataset.originalText || 'Load More';
    }
  }
}

customElements.define('load-more-advisory', LoadMoreAdvisory);